/** Shared person/license-row shaping — used by both pages/Licenses.tsx (all services) and
 * components/ServiceLicensesTab.tsx (one service), so the two stay in sync automatically. */
import type { LicenseTypeConfig, OrphanCheckResult, PersonSummary, ResolutionKind } from '../types'

export type EntraStatus = OrphanCheckResult['entraStatus']

export interface PersonRow {
  personEmail: string
  personDisplayName: string
  entraStatus: EntraStatus
  department?: string
  /** Entra `userType` ('Member'/'Guest'), when this row resolved to a Person; null/undefined for
   * accounts with no matching Entra person. Drives the "Show guests" filter together with the
   * personEmail domain. */
  userType?: string | null
  responsibleEmail?: string
  responsibleDisplayName?: string
  /** True when this override names a responsibleEmail whose Person is disabled/gone */
  responsibleNeedsReview: boolean
  comment?: string
  /** The raw account's role on the service (e.g. Zoom's Owner/Admin/Member), if reported */
  role?: string
  /** How the raw account signs in (e.g. Zoom's "Single Sign-On"/"Microsoft"/"Work Email") */
  loginType?: string
  /** Kyiv-formatted last-login timestamp, if reported */
  lastLoginAt?: string
  /** The account's status in the vendor's own system (e.g. Atlassian 'active'/'inactive'/'closed'),
   * if reported. A non-'active' value while the account still holds a license is "blocked in
   * service". */
  accountStatus?: string
  byService: Record<string, OrphanCheckResult[]>
  /** The row's own identifying email. Equal to personEmail when grouped by person (the default);
   * when grouped by raw email (see buildRows' groupByRawEmail option), this is the raw
   * service-reported account, which for a 'manual' (Linked) row differs from personEmail — the
   * raw account and the Entra person it's linked to are two different addresses by definition. */
  rawEmail: string
  resolutionKind: ResolutionKind
}

/**
 * Starts from every known Person (so people with zero licenses are included) and layers
 * assignments on top. An assignment for an email with no Person row at all (fully removed from
 * Entra) still needs its own row — that's the "Missing in Entra" orphan signal.
 *
 * By default groups by the resolved personEmail, so a manually-linked account's license counts
 * toward the person it's linked to (see hasLinkedAccounts for surfacing that it happened) — this
 * is what the all-services Licenses page wants, one row per person. Pass groupByRawEmail: true
 * (what the per-service Licenses tab wants) to instead give a manually-linked raw account its own
 * row, distinct from the person it resolves to — useful there since that tab is about browsing
 * this service's own accounts, not a person's overall portfolio.
 */
export function buildRows(
  persons: PersonSummary[],
  results: OrphanCheckResult[],
  options: { groupByRawEmail?: boolean } = {},
): PersonRow[] {
  const rows = new Map<string, PersonRow>()

  for (const p of persons) {
    rows.set(p.email, {
      personEmail: p.email,
      personDisplayName: p.displayName,
      entraStatus: p.entraStatus,
      department: p.department,
      userType: p.userType,
      responsibleNeedsReview: false,
      byService: {},
      rawEmail: p.email,
      resolutionKind: 'entra',
    })
  }

  for (const r of results) {
    const key = options.groupByRawEmail ? r.rawEmail : r.personEmail
    let row = rows.get(key)
    if (!row) {
      row = {
        personEmail: r.personEmail,
        personDisplayName: r.personDisplayName,
        entraStatus: r.entraStatus,
        department: r.department,
        responsibleEmail: r.responsibleEmail,
        responsibleDisplayName: r.responsibleDisplayName,
        responsibleNeedsReview: r.responsibleNeedsReview,
        comment: r.comment,
        role: r.role,
        loginType: r.loginType,
        lastLoginAt: r.lastLoginAt,
        accountStatus: r.accountStatus,
        byService: {},
        rawEmail: r.rawEmail,
        resolutionKind: r.resolutionKind,
      }
      rows.set(key, row)
    } else {
      // A row may already exist here from the known-Person pre-seed above, defaulted to
      // resolutionKind 'entra' with no override info. That default is stale the moment a real
      // assignment for this exact key comes in with an actual override (e.g. groupByRawEmail:
      // true and this raw email — which happens to also be a real person's own address — was
      // manually linked elsewhere) — refresh the row's identity fields from the assignment so the
      // override isn't masked by the pre-seed default.
      row.personEmail = r.personEmail
      row.personDisplayName = r.personDisplayName
      row.entraStatus = r.entraStatus
      row.department = r.department
      row.responsibleEmail = r.responsibleEmail
      row.responsibleDisplayName = r.responsibleDisplayName
      row.responsibleNeedsReview = r.responsibleNeedsReview
      row.comment = r.comment
      row.role = r.role
      row.loginType = r.loginType
      row.lastLoginAt = r.lastLoginAt
      row.accountStatus = r.accountStatus
      row.resolutionKind = r.resolutionKind
    }
    (row.byService[r.serviceId] ??= []).push(r)

    // Also attribute a shared/external account's license to its named Responsible person's row —
    // mirrors how a manually-linked account's license already lands under its target via the
    // personEmail reassignment findOrphans() does for 'manual'. Only in person-grouped mode (the
    // per-service raw-email view already shows these under their own account and doesn't need the
    // duplication); skipped when the responsible person's row is this same row already.
    if (!options.groupByRawEmail && r.responsibleEmail && (r.resolutionKind === 'shared' || r.resolutionKind === 'external')) {
      const responsibleRow = rows.get(r.responsibleEmail)
      if (responsibleRow && responsibleRow !== row) {
        (responsibleRow.byService[r.serviceId] ??= []).push(r)
      }
    }
  }

  return [...rows.values()].sort((a, b) => a.personDisplayName.localeCompare(b.personDisplayName))
}

/** Excludes the synthetic "no trackable license" rows a module can emit to keep a free-tier-only
 * account visible for role/login-type reporting (see OrphanCheckResult.excludeFromSeatCount) —
 * not a real license, so it must not count as "has a license" here. */
export function hasAnyLicense(row: PersonRow): boolean {
  return Object.values(row.byService).some(assignments => assignments.some(a => !a.excludeFromSeatCount))
}

/** Every assignment this person holds has been explicitly dismissed as a non-employee account */
export function isFullyExternal(row: PersonRow): boolean {
  const assignments = Object.values(row.byService).flat()
  return assignments.length > 0 && assignments.every(a => a.resolutionKind === 'external')
}

/** True when an assignment is shown under a row it wasn't reported against directly — a manual
 * Link override (always), or a shared/external account cross-attributed to its named Responsible
 * person's row (see buildRows above). Deliberately excludes plain 'entra' matches: an alias
 * (proxyAddress) match also has `rawEmail !== personEmail` (the service reported the alias, Entra
 * resolved it to the person's primary email) but is automatic resolution, not an override — it
 * must never trip the "attributed from elsewhere" signal below. */
export function isAttributedElsewhere(a: OrphanCheckResult, rowPersonEmail: string): boolean {
  return a.resolutionKind !== 'entra' && a.rawEmail !== rowPersonEmail
}

/** One badge per override kind next to the status, each with its own icon/color instead of one
 * shared chain-link badge for all three — Linked/Shared/External read as different things and
 * are easy to conflate otherwise. */
export function hasLinkedAccounts(row: PersonRow): boolean {
  return Object.values(row.byService).flat().some(a => a.resolutionKind === 'manual' && isAttributedElsewhere(a, row.personEmail))
}
export function hasSharedAccounts(row: PersonRow): boolean {
  return Object.values(row.byService).flat().some(a => a.resolutionKind === 'shared' && isAttributedElsewhere(a, row.personEmail))
}
export function hasExternalAccounts(row: PersonRow): boolean {
  return Object.values(row.byService).flat().some(a => a.resolutionKind === 'external' && isAttributedElsewhere(a, row.personEmail))
}

/** Mirrors UserLinksTab's own isMissingOwner predicate (and the Dashboard's
 * missingOwnerCount) exactly — a row resolves to a real/known identity (not 'external', not
 * fully unresolved), but has no department and no named responsible person, so nobody's
 * accountable for it. 'external' is exempt: marking it external is itself the ownership
 * decision. A row with no licenses at all is exempt too — there's nothing to own yet. */
export function isMissingOwner(row: PersonRow): boolean {
  return (
    hasAnyLicense(row) &&
    !isFullyExternal(row) &&
    row.resolutionKind !== 'unresolved' &&
    row.resolutionKind !== 'external' &&
    !row.department &&
    !row.responsibleEmail
  )
}

/** Mirrors the Dashboard's `blockedCount` predicate exactly — the core "orphaned license"
 * problem this tool exists to catch: a real Entra person this resolved to (not 'external'/
 * 'shared', which have no live individual behind them by design) who is now blocked/disabled,
 * while the license is still allocated to them. Requires at least one real (non-synthetic)
 * license so a free-tier-only account's placeholder row doesn't count — matching the Dashboard's
 * account-based count. */
export function isBlocked(row: PersonRow): boolean {
  return (
    hasAnyLicense(row) &&
    row.entraStatus === 'disabled' &&
    row.resolutionKind !== 'external' &&
    row.resolutionKind !== 'shared'
  )
}

/** Mirrors the Dashboard's `notSsoCount` predicate exactly — an account signing in via the
 * vendor's own local password rather than the org's identity provider. Only ever true for a
 * module that reports `loginType` (currently zoom) — undefined means "this service doesn't
 * report login method", not "not using SSO". */
export function isNotSso(row: PersonRow): boolean {
  return (
    row.resolutionKind !== 'external' &&
    row.loginType !== undefined &&
    row.loginType !== 'Single Sign-On' &&
    row.loginType !== 'Microsoft'
  )
}

/** Mirrors the Dashboard's `serviceBlockedCount` — an account disabled in the vendor's own system
 * (e.g. Atlassian 'inactive'/'closed') that still holds a license, a wasted seat. */
export function isServiceBlocked(row: PersonRow): boolean {
  return hasAnyLicense(row) && row.accountStatus !== undefined && row.accountStatus !== 'active'
}

/** `lastLoginAt` is a Kyiv-formatted "YYYY-MM-DDTHH:mm:ss" string (see api/lib/kyivTime.ts) — the
 * cutoff must be formatted the same way, not the browser's local time or a UTC ISO string,
 * otherwise the comparison is off by the browser's/Kyiv's UTC offset. */
function kyivCutoffString(daysAgo: number): string {
  const date = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000)
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Kyiv',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(date)
  const get = (type: string) => parts.find(p => p.type === type)!.value
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`
}

/** Mirrors the Dashboard's `staleLoginCount` predicate exactly — a paid seat with no login in
 * staleLoginDays (or ever). Only ever true for a module that reports `loginType` (currently
 * zoom, alongside lastLoginAt) — see isNotSso's note on why that field gates this. */
export function isStale(row: PersonRow, staleLoginDays: number): boolean {
  if (row.resolutionKind === 'external' || row.loginType === undefined) return false
  if (!row.lastLoginAt) return true
  return row.lastLoginAt < kyivCutoffString(staleLoginDays)
}

export const RESOLUTION_INFO: Record<ResolutionKind, { label: string; color: 'success' | 'brand' | 'informative' | 'warning' | 'danger'; hint: string }> = {
  entra: { label: 'Entra-matched', color: 'success', hint: 'Automatically matched to an active person in Entra ID — no manual action taken or needed.' },
  manual: { label: 'Manually linked', color: 'brand', hint: 'An admin manually linked this raw email to a specific Entra person via "Link".' },
  external: { label: 'External', color: 'informative', hint: 'An admin marked this as a known non-employee contact via "Mark external".' },
  shared: { label: 'Shared', color: 'warning', hint: 'An admin marked this as a shared/departmental account via "Mark shared".' },
  unresolved: { label: 'Unresolved', color: 'danger', hint: 'No match in Entra and no manual override yet — needs attention.' },
}

export type ProblemFilter = 'all' | 'blocked' | 'serviceBlocked' | 'unresolved' | 'needsReview' | 'missingOwner' | 'linked' | 'duplicate' | 'notSso' | 'stale'

/** The same problem-filter set on both the Licenses tab and the User links tab (per-service) —
 * one definition, so the two never drift into showing different things for the same label.
 * `duplicateKeys` comes from findDuplicateLicenseKeys() over the tab's own rows, since duplicate
 * detection needs the full assignment set, not a single row. */
export function buildProblemFilters(
  staleLoginDays: number,
  duplicateKeys: Set<string> = new Set(),
): { value: ProblemFilter; label: string; predicate: (row: PersonRow) => boolean }[] {
  return [
    { value: 'all', label: 'All', predicate: () => true },
    { value: 'blocked', label: 'Blocked', predicate: isBlocked },
    { value: 'serviceBlocked', label: 'Blocked in service', predicate: isServiceBlocked },
    // unresolved/needsReview are gated on a real license too, matching the Dashboard's counts —
    // a free-tier-only account's synthetic row shouldn't show up as an unresolved/orphan problem.
    { value: 'unresolved', label: 'Unresolved', predicate: row => hasAnyLicense(row) && row.resolutionKind === 'unresolved' },
    { value: 'needsReview', label: 'Needs review', predicate: row => hasAnyLicense(row) && row.responsibleNeedsReview },
    { value: 'missingOwner', label: 'Missing owner', predicate: isMissingOwner },
    { value: 'linked', label: 'Linked', predicate: row => row.resolutionKind === 'manual' },
    { value: 'duplicate', label: 'Duplicate', predicate: row => hasDuplicateLicense(row, duplicateKeys) },
    { value: 'notSso', label: 'Not SSO', predicate: isNotSso },
    { value: 'stale', label: 'Stale login', predicate: row => isStale(row, staleLoginDays) },
  ]
}

/** Key identifying "this resolved person holds this license type on this service" — used to spot
 * the same person holding the same license type more than once (typically via a primary email
 * plus an alias, each its own ServiceAssignment/row under groupByRawEmail) — a wasted/duplicate
 * seat, unrelated to Entra status or resolution overrides. */
export function licenseKey(a: OrphanCheckResult): string {
  return `${a.serviceId}::${a.personEmail}::${a.licenseType}`
}

/** The set of licenseKey() values held more than once across the given results — pass the full,
 * un-grouped results (not a single row's byService) so duplicates across different raw
 * emails/aliases for the same person are actually visible. Only considers 'entra' (automatic,
 * no override) assignments — a manually-linked/shared/external account deliberately mirrors
 * someone's license (e.g. a test account kept in sync with its owner's real one), so the same
 * license type showing up there too isn't a wasted seat, it's the intended setup. */
export function findDuplicateLicenseKeys(results: OrphanCheckResult[]): Set<string> {
  const counts = new Map<string, number>()
  for (const r of results) {
    if (r.resolutionKind !== 'entra' || r.excludeFromSeatCount) continue
    const key = licenseKey(r)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return new Set([...counts].filter(([, count]) => count > 1).map(([key]) => key))
}

/** True when any real (non-synthetic) assignment on this row participates in a duplicate — same
 * predicate the Dashboard's account-based `duplicateLicenseCount` mirrors, so the "Duplicate"
 * filter chip and the Dashboard button agree. */
export function hasDuplicateLicense(row: PersonRow, duplicateKeys: Set<string>): boolean {
  return Object.values(row.byService)
    .flat()
    .some(a => !a.excludeFromSeatCount && duplicateKeys.has(licenseKey(a)))
}

/** Friendly name for a license type if any service has one configured, else the raw SKU string */
export function licenseTypeDisplayName(
  licenseType: string,
  configByService: Record<string, Record<string, LicenseTypeConfig>>,
): string {
  for (const configs of Object.values(configByService)) {
    if (configs[licenseType]?.displayName) return configs[licenseType].displayName!
  }
  return licenseType
}

export interface StoredFilters {
  search: string
  statusFilter: string[]
  licenseFilter: string[]
  showNoLicense: boolean
  /** Show external/guest accounts (Entra Guests, plus accounts with no Entra person on a
   * non-internal email domain). Off by default so an Atlassian import's external users don't
   * flood the Licenses list. */
  showGuests: boolean
  /** Only meaningful on pages/Licenses.tsx (LicenseFilterBar's showAttributionToggle prop) — hides
   * shared/external-attributed licenses from the per-service icon cells. Unused but harmless on
   * ServiceLicensesTab, which doesn't render the toggle. */
  hideAttributedLicenses: boolean
}

export const DEFAULT_FILTERS: StoredFilters = {
  search: '',
  statusFilter: [],
  licenseFilter: [],
  showNoLicense: false,
  showGuests: false,
  hideAttributedLicenses: false,
}

function emailDomain(email: string): string {
  return email.split('@')[1]?.toLowerCase() ?? ''
}

/** Domains of every account Entra reports as a Member — the org's "internal" domains. Used to
 * classify accounts that aren't in Entra at all. */
export function internalDomains(rows: PersonRow[]): Set<string> {
  const domains = new Set<string>()
  for (const row of rows) {
    if (row.userType?.toLowerCase() === 'member') domains.add(emailDomain(row.personEmail))
  }
  return domains
}

/** A "guest" (hidden unless the "Show guests" toggle is on) is either:
 * - an Entra Guest (`userType === 'Guest'`), or
 * - an account with no matching Entra person at all whose email domain isn't an internal
 *   (Member) domain — an external Atlassian user, for example.
 * Kept in sync with filterPersonRows' `showGuests`. */
export function isGuest(row: PersonRow, internal: Set<string>): boolean {
  if (row.userType) return row.userType.toLowerCase() === 'guest'
  const domain = emailDomain(row.personEmail)
  return domain !== '' && !internal.has(domain)
}

export function filterPersonRows(rows: PersonRow[], filters: StoredFilters): PersonRow[] {
  const query = filters.search.trim().toLowerCase()
  const internal = internalDomains(rows)
  return rows.filter(row => {
    if (!filters.showGuests && isGuest(row, internal)) return false
    if (!filters.showNoLicense && !hasAnyLicense(row)) return false
    if (
      query &&
      !row.personDisplayName.toLowerCase().includes(query) &&
      !row.personEmail.toLowerCase().includes(query) &&
      !row.rawEmail.toLowerCase().includes(query)
    ) {
      return false
    }
    if (filters.statusFilter.length && !filters.statusFilter.includes(row.entraStatus)) return false
    if (filters.licenseFilter.length) {
      const rowLicenseTypes = Object.values(row.byService).flat().map(a => a.licenseType)
      if (!rowLicenseTypes.some(lt => filters.licenseFilter.includes(lt))) return false
    }
    return true
  })
}
